// src/components/ComplianceSection.js
// Compliance checklist panel - rendered for medium and ftc-enhanced tier orders.
// Shows automatic validation results and the interactive checklist with user
// attribution. Per-vehicle compliance detail inputs live in
// ComplianceVehicleDetails, rendered beneath the Vehicle Offers section.

import React, { useMemo } from 'react';
import { getAuth } from 'firebase/auth';
import Section from './Section';
import {
  getChecklistForTier,
  runAutoChecks,
  getChecklistProgress,
} from '../utils/ftcCompliance';

function ComplianceSection({
  vehicles,
  prizes,
  orderDetails,
  dealershipInfo,
  checklist,
  setChecklist,
  tier = 'ftc-enhanced',
}) {
  const autoResults = useMemo(
    () => runAutoChecks(vehicles, prizes, orderDetails, dealershipInfo, tier),
    [vehicles, prizes, orderDetails, dealershipInfo, tier]
  );

  const progress = useMemo(
    () => getChecklistProgress(checklist, autoResults, tier),
    [checklist, autoResults, tier]
  );

  const checklistSections = useMemo(() => getChecklistForTier(tier), [tier]);

  const getCurrentUser = () => {
    try {
      const user = getAuth().currentUser;
      return user?.displayName || user?.email || 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  const handleToggle = (itemId) => {
    setChecklist((prev) => {
      const current = prev[itemId];
      if (current?.checked) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return {
        ...prev,
        [itemId]: { checked: true, by: getCurrentUser(), at: new Date().toISOString() },
      };
    });
  };

  const enhanced = tier === 'ftc-enhanced';

  return (
    <Section title={enhanced ? 'FTC Compliance' : 'Compliance Checklist (Medium)'}>
      {/* Progress header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
          padding: '10px 14px',
          background: progress.complete ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${progress.complete ? '#10b981' : '#ef4444'}`,
          borderRadius: '8px',
        }}
      >
        <span style={{ fontSize: '20px' }}>{progress.complete ? '✅' : '⛔'}</span>
        <div>
          <div style={{ fontWeight: '700', fontSize: '14px', color: progress.complete ? '#065f46' : '#991b1b' }}>
            {progress.done} of {progress.required} checklist items complete
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {progress.complete
              ? enhanced
                ? 'This order meets the FTC vendor certification checklist.'
                : 'This order meets the medium compliance checklist.'
              : 'All items must pass or be checked off before this order should be sent for approval.'}
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div>
        {checklistSections.map((section) => {
          // Hide lease/finance-only sections when not applicable
          if (section.appliesTo === 'lease' && !vehicles.some((v) => v.financeType === 'lease' && (v.make || v.model))) return null;
          if (section.appliesTo === 'finance' && !vehicles.some((v) => v.financeType !== 'lease' && (v.make || v.model))) return null;
          return (
            <div key={section.section} style={{ marginBottom: '14px' }}>
              <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#374151' }}>
                {section.section}
              </div>
              {section.items.map((item) => {
                if (item.type === 'auto') {
                  const res = autoResults[item.id];
                  if (!res || res.pass === null) return null;
                  return (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'flex-start',
                        padding: '6px 8px',
                        marginBottom: '4px',
                        borderRadius: '6px',
                        background: res.pass ? '#f0fdf4' : '#fef2f2',
                        border: `1px solid ${res.pass ? '#bbf7d0' : '#fecaca'}`,
                        fontSize: '12px',
                      }}
                    >
                      <span>{res.pass ? '✅' : '❌'}</span>
                      <div>
                        <div style={{ fontWeight: '500', color: '#111827' }}>
                          {item.text}{' '}
                          <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '600' }}>AUTO</span>
                        </div>
                        <div style={{ color: res.pass ? '#166534' : '#991b1b' }}>{res.message}</div>
                      </div>
                    </div>
                  );
                }
                const state = checklist[item.id];
                return (
                  <label
                    key={item.id}
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'flex-start',
                      padding: '6px 8px',
                      marginBottom: '4px',
                      borderRadius: '6px',
                      background: state?.checked ? '#f0fdf4' : '#ffffff',
                      border: `1px solid ${state?.checked ? '#bbf7d0' : '#e5e7eb'}`,
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!state?.checked}
                      onChange={() => handleToggle(item.id)}
                      style={{ marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '500', color: '#111827' }}>{item.text}</div>
                      {state?.checked && (
                        <div style={{ color: '#6b7280', fontSize: '11px' }}>
                          Checked by {state.by} on {new Date(state.at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic', marginTop: '8px' }}>
        Remember to press "Save Changes" on the order to store checklist progress.
      </div>
    </Section>
  );
}

export default ComplianceSection;